import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { ArrowDownToLine, X as XIcon } from "lucide-react";

const INSTALLED_KEY = "dcc-apk-installed";
const DISMISS_KEY = "dcc-apk-update-dismissed";
const DISMISS_HOURS = 24;
const CHECK_INTERVAL_MS = 4 * 60 * 60 * 1000; // re-check every 4 hours

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

/* ── Helpers ── */

function getIsAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent);
}

/** Read the stored installed-version record from localStorage. */
function getInstalledInfo() {
  try {
    const raw = localStorage.getItem(INSTALLED_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Write the installed-version record. */
export function setInstalledInfo(versionCode, versionName) {
  try {
    localStorage.setItem(
      INSTALLED_KEY,
      JSON.stringify({ versionCode, versionName, ts: Date.now() })
    );
  } catch {
    /* private browsing */
  }
}

/** Check whether the dismiss cooldown is still active. */
function isDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < DISMISS_HOURS * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

/* ═══════════════════════════════════════
   Main component
   ═══════════════════════════════════════ */

/**
 * Checks the server for a newer Android APK and prompts the user to update.
 *
 * Logic:
 *  - Only shown on Android devices.
 *  - Fetches GET /api/app/version to learn the latest versionCode.
 *  - Compares against the versionCode stored in localStorage when the user
 *    last downloaded the APK (set by InstallAppCard or this component).
 *  - If no stored version → silently seeds localStorage with the server value
 *    (assumes user already has the latest).
 *  - If stored versionCode < server versionCode → show update banner.
 *  - "Update" button links to the APK download, then stores the new version.
 *  - "Later" dismisses for 24 hours.
 */
export function ApkUpdateBanner() {
  const [serverVersion, setServerVersion] = useState(null);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const isAndroid = getIsAndroid();

  /** Fetch latest version from server, compare, decide. */
  const checkVersion = useCallback(async () => {
    try {
      const res = await fetch(`${backendUrl}/api/app/version`);
      if (!res.ok) return;
      const data = await res.json();
      setServerVersion(data);

      const installed = getInstalledInfo();
      if (!installed) {
        // First visit — seed with current server version (assume up-to-date)
        setInstalledInfo(data.versionCode, data.versionName);
        return;
      }

      if (data.versionCode > installed.versionCode) {
        setHasUpdate(true);
      }
    } catch {
      /* network error — silently skip */
    }
  }, []);

  useEffect(() => {
    if (!isAndroid) return;
    if (isDismissed()) {
      setDismissed(true);
      return;
    }

    // Initial check
    checkVersion();

    // Re-check when app returns from background
    function onVisible() {
      if (document.visibilityState === "visible") {
        if (!isDismissed()) checkVersion();
      }
    }
    document.addEventListener("visibilitychange", onVisible);

    // Periodic re-check while the page stays open
    const interval = setInterval(() => {
      if (!isDismissed()) checkVersion();
    }, CHECK_INTERVAL_MS);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(interval);
    };
  }, [isAndroid, checkVersion]);

  /* ── Hide conditions ── */
  if (!isAndroid || !hasUpdate || dismissed || !serverVersion) return null;

  function handleDismiss() {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* private browsing */
    }
  }

  function handleUpdate() {
    setDownloading(true);
    // Store the new version now — the download link fires immediately
    setInstalledInfo(serverVersion.versionCode, serverVersion.versionName);

    // Small delay so the UI shows "Downloading…" before navigation
    setTimeout(() => {
      window.location.href = `${backendUrl}${serverVersion.downloadUrl}`;
      // After a beat, reset UI so the banner hides on next render
      setTimeout(() => {
        setHasUpdate(false);
        setDownloading(false);
      }, 2000);
    }, 150);
  }

  return (
    <div
      data-testid="apk-update-banner"
      className="relative flex items-center gap-3 px-4 py-2.5 mb-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--accent)/0.08)]"
    >
      {/* Icon */}
      <div className="shrink-0 h-9 w-9 rounded-lg bg-[hsl(var(--accent)/0.15)] flex items-center justify-center">
        <ArrowDownToLine className="h-4 w-4 text-[hsl(var(--accent))]" />
      </div>

      {/* Copy */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-foreground leading-tight">
          App update available
        </div>
        <div className="text-xs text-muted-foreground leading-tight mt-0.5">
          Version {serverVersion.versionName} is ready to install
        </div>
      </div>

      {/* Update button */}
      <Button
        onClick={handleUpdate}
        disabled={downloading}
        size="sm"
        className="shrink-0 h-9 px-4 rounded-lg bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-[hsl(var(--accent-foreground))] font-semibold text-xs"
        data-testid="apk-update-button"
      >
        <ArrowDownToLine
          className={`h-3.5 w-3.5 mr-1.5 ${downloading ? "animate-bounce" : ""}`}
        />
        {downloading ? "Downloading…" : "Update"}
      </Button>

      {/* Dismiss */}
      <button
        onClick={handleDismiss}
        className="shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--secondary))] transition-colors"
        aria-label="Dismiss update banner"
        data-testid="apk-update-dismiss"
      >
        <XIcon className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
